import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createAppUser,
  createFbAppUserFromUserCredential,
  getUserDataFromUserCredential,
  loadAppUser,
  updateFbAppUser,
} from 'api/UserAPI';
import {
  ActionCls,
  ArmyGreenCls,
  Cls,
  CrimsonCls,
  Emojis,
  InfoCls,
  LocationCls,
  ValueCls,
  ValueLgCls,
} from 'app/constants';
import { setEndAppInitialization, setSelectedScene } from 'app/slices/NavigationSlice';
import { memoize } from 'proxy-memoize';
import { AppScene, AppThunk, AppUser, Maybe, RootState } from 'types';
import {
  analytics,
  auth,
  createUserWithEmailAndPassword,
  logEvent,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
} from '../../lib/firebase/Firebase';

export enum UserAuthState {
  Init = 'Init',
  Authenticating = 'Authenticating',
  LoggedIn = 'LoggedIn',
  NotLoggedIn = 'NotLoggedIn',
  LoggedOut = 'LoggedOut',
  PasswordReset = 'PasswordReset',
  Error = 'Error',
}

export interface UserProps extends AppUser {
  isLoading: boolean;
  authState: UserAuthState;
  isAuthStateObserverMounted: boolean;
  isVerifying: boolean;
  emailVerificationSentDate: number;
  emailVerifiedDate: number;
  errorMessage: string;
}

const initialState: UserProps = {
  isLoading: false,
  authState: UserAuthState.Authenticating,
  isAuthStateObserverMounted: false,
  uid: '',
  email: '',
  name: '',
  isAnonymous: false,
  emailVerified: false,
  isVerifying: false,
  emailVerificationSentDate: 0,
  emailVerifiedDate: 0,
  isEditor: false,
  createdOn: 0,
  modifiedOn: 0,
  errorMessage: '',
};

type EmailVerifiedPayload = { isVerified: boolean; emailVerifiedDate: number };
type EmailVerifyingPayload = { isVerifying: boolean; emailVerifyingDate?: number };

const handleErrorCase = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/network-request-failed':
      return 'No Internet';
    default:
      return 'Error';
  }
};

// #region USER ACCOUNT ACTIONS ***********************************************
export const verifyUserAccount = (): AppThunk => (dispatch) => {
  if (auth.currentUser) {
    sendEmailVerification(auth.currentUser);
    dispatch(setEmailVerifying({ isVerifying: true, emailVerifyingDate: Date.now() }));
  } else {
    console.log('%c ERROR %c | doSendVerificationEmail | no auth.currentUser', ActionCls, '');
  }
};

export const doLogoutUser = (): AppThunk => (dispatch) => {
  dispatch(setUserAuthState(UserAuthState.LoggedOut));
  signOut(auth)
    .then(() => {
      dispatch(setUserAuthState(UserAuthState.LoggedOut));
      dispatch(setSelectedScene(AppScene.Home));
    })
    .catch((reason) => {
      console.log(`${Emojis.eyes} %c LOGOUT  %c, ${reason}`, CrimsonCls, '');
      logEvent(analytics, 'exception', { description: `method: performUserLogout, reason: ${reason}` });
    });
};

export const doCreateAccount =
  (email: string, password: string): AppThunk =>
  (dispatch) => {
    dispatch(setUserAuthState(UserAuthState.Authenticating));
    // dispatch(setCreateAccountFormError(''));
    createUserWithEmailAndPassword(auth, email, password)
      .then(async (userModel: UserCredential) => {
        const newUser: AppUser = await createFbAppUserFromUserCredential({
          ...userModel,
          user: { ...userModel.user },
        });

        dispatch(setAppUser(newUser));
        // dispatch(setToastNotification(getToastSuccessPayload('Account Created')));

        // TODO: @Andrey11 send account email verification
        // dispatch(doSendVerificationEmail());
      })
      .catch((reason) => {
        console.log(`performCreateAccount Error ${reason}`);
        dispatch(setUserAuthState(UserAuthState.Error));
        dispatch(setErrorMessage(handleErrorCase(reason.code)));
        logEvent(analytics, 'exception', { description: `method: performCreateAccount, reason: ${reason}` });
        // dispatch(setCreateAccountFormError(reason.code));
        // dispatch(setUserStatus('idle'));
      });
  };

export const doLoginWithEmailAndPassword =
  (email: string, password: string): AppThunk =>
  (dispatch) => {
    dispatch(setUserAuthState(UserAuthState.Authenticating));
    const authMsg = `${auth.name} ${auth.currentUser ? 'has currentUser' : 'no currentUser'}`;
    const msg = `${Emojis.eyes} %c LOGIN  %c, %c ${email} %c %c ${password} %c %c ${authMsg} %c`;
    console.log(msg, Cls.Loc, '', Cls.Val, '', Cls.DrkGn, '', Cls.Info, '');

    signInWithEmailAndPassword(auth, email, password)
      .then((userModel: UserCredential) => {
        dispatch(setUserAuthState(UserAuthState.LoggedIn));
        const maybeEmail = userModel.user.email ?? 'no email';
        console.log(`${Emojis.checkMark} %c LOGGED IN %c %c ${maybeEmail} %c`, ArmyGreenCls, '', ValueCls, '');
      })
      .catch((reason) => {
        console.log(`${Emojis.warning} %c LOGIN ERROR %c`, CrimsonCls, '');
        console.log(`${Emojis.warning} %c ${reason.code} %c`, ValueCls, '');

        dispatch(setUserAuthState(UserAuthState.Error));
        dispatch(setErrorMessage(handleErrorCase(reason.code)));
      });
  };

export const updateUserName =
  (name: string): AppThunk =>
  (dispatch, getState) => {
    if (name !== getState().user.name) {
      const user = { ...getState().user, name };
      updateFbAppUser(user);
      dispatch(setName(name));
    }
  };

export const doResetUserPassword =
  (email: string): AppThunk =>
  (dispatch) => {
    sendPasswordResetEmail(auth, email)
      .then(() => {
        dispatch(setUserAuthState(UserAuthState.PasswordReset));
        console.log(`${Emojis.checkMark} %c RESET EMAIL SENT %c`, ArmyGreenCls, '');
      })
      .catch((reason) => {
        console.log(`${Emojis.warning} %c RESET EMAIL ERROR %c`, CrimsonCls, '');
        console.log(`${Emojis.warning} %c ${reason.code} %c`, ValueCls, '');
        dispatch(setErrorMessage(handleErrorCase(reason.code)));
        dispatch(setUserAuthState(UserAuthState.Error));
      });
  };
// #endregion

export const subscribeUserStatusChange = (): AppThunk => (dispatch, getState) => {
  const isMounted = getState().user.isAuthStateObserverMounted;

  if (!isMounted) {
    console.log(`${Emojis.eyes} %c subscribeUserStatusChange %c`, LocationCls, '');
    dispatch(setIsAuthStateObserverMounted(true));
    dispatch(setIsLoading(true));
    dispatch(setUserAuthState(UserAuthState.Authenticating));

    auth.onAuthStateChanged(async (userModel: User | null) => {
      if (userModel) {
        const uid = userModel.uid;
        const verified = userModel.emailVerified;
        const verifiedStatus = `${verified ? '' : 'not '}verified`;
        const msg = `%c onAuthStateChanged %c %c ${uid} %c %c ${userModel.email} %c %c ${verifiedStatus} %c`;
        console.log(`${Emojis.eyes} ${msg}`, LocationCls, '', ValueCls, '', ValueLgCls, '', ValueCls, '');
        const usr = getUserDataFromUserCredential(userModel);

        // #region  ONLINE/OFFLINE status
        // if (getState().user.userStatus === 'creatingAccount') {
        // isPrint(PRINT_FLAG) && console.log(...printMsg('onAuthStateChanged', 'creatingAccount', 'exiting this flow'));
        // return;
        // }
        // if (getState().ui.appUi.appStatus === AppStatus.Offline) {
        // if (getState().user.userStatus === 'signingIn' && getState().ui.userUi.showLoginForm) {
        // dispatch(setLoginFormError('app/offline'));
        // }
        // dispatch(setUserStatus('notSignedIn'));
        // dispatch(setToastMessage({ message: 'App is OFFLINE', type: AppNotification.Warning }));
        // dispatch(setShowToast(true));
        // return;
        // }
        // #endregion

        try {
          let user = await loadAppUser(uid);
          if (user) {
            if (user.emailVerified !== usr.emailVerified && usr.emailVerified) {
              console.log(`${Emojis.eyes} Email verified status should be %c ${usr.emailVerified} %c`, ValueCls, '');
              user = { ...user, emailVerified: true, modifiedOn: usr.modifiedOn };
              updateFbAppUser(user);
            } else {
              console.log(`${Emojis.eyes} Email verified status is %c ${user.emailVerified} %c`, ValueCls, '');
            }

            // As part of `setAppUser` action here
            // `userAuthState` will be set to `UserAuthState.LoggedIn`
            dispatch(setAppUser(user));
            dispatch(setIsLoading(false));
            dispatch(setEndAppInitialization());
          } else {
            dispatch(setUserAuthState(UserAuthState.NotLoggedIn));
            dispatch(setIsLoading(false));
            dispatch(setEndAppInitialization());
            dispatch(resetAppUser());
          }
        } catch (error: any) {
          console.log(`Error has occurred: ${error}`);
          console.dir(error);

          if (uid && userModel.email && error.message === 'USER_NOT_ADDED') {
            const newUser: AppUser = await createAppUser(userModel);
            dispatch(setAppUser(newUser));
            dispatch(setIsLoading(false));
            dispatch(setEndAppInitialization());
          } else {
            dispatch(resetAppUser());
            dispatch(setIsLoading(false));
            dispatch(setUserAuthState(UserAuthState.Error));
          }
        }
      } else {
        console.log(`${Emojis.asterisk} %c No userModel %c logged out %c`, InfoCls, '', ActionCls, '');
        dispatch(resetAppUser());
      }
    });
  }
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUserAuthState: (state, action: PayloadAction<UserAuthState>) => {
      state.authState = action.payload;
    },
    setIsAuthStateObserverMounted: (state, action: PayloadAction<boolean>) => {
      state.isAuthStateObserverMounted = action.payload;
    },
    setAppUser: (state, action: PayloadAction<AppUser>) => {
      state.isLoading = false;
      state.authState = action.payload.uid && action.payload.email ? UserAuthState.LoggedIn : UserAuthState.LoggedOut;
      state.uid = action.payload.uid;
      state.email = action.payload.email;
      state.name = action.payload.name ?? '';
      state.isAnonymous = false;
      state.emailVerified = action.payload.emailVerified;
      state.isEditor = action.payload.isEditor;
      state.createdOn = action.payload.createdOn;
      state.modifiedOn = action.payload.modifiedOn;
    },
    setUid: (state, action: PayloadAction<string>) => {
      state.uid = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setIsAnonymousUser: (state, action: PayloadAction<boolean>) => {
      state.isAnonymous = action.payload;
    },
    setIsEditor: (state, action: PayloadAction<boolean>) => {
      state.isEditor = action.payload;
    },
    setCreatedOn: (state, action: PayloadAction<number>) => {
      state.createdOn = action.payload;
    },
    setModifiedOn: (state, action: PayloadAction<number>) => {
      state.modifiedOn = action.payload;
    },
    setEmailVerified: (state, action: PayloadAction<EmailVerifiedPayload>) => {
      state.isVerifying = false;
      state.emailVerified = action.payload.isVerified;
      state.emailVerifiedDate = action.payload.emailVerifiedDate;
    },
    setEmailVerifying: (state, action: PayloadAction<EmailVerifyingPayload>) => {
      state.isVerifying = action.payload.isVerifying;
      state.emailVerificationSentDate = action.payload.emailVerifyingDate ?? state.emailVerificationSentDate;
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    // RESET ******************************************************************
    resetAppUser: (state) => {
      return {
        ...initialState,
        isAuthStateObserverMounted: state.isAuthStateObserverMounted,
        authState: state.authState,
      };
    },
  },
});

export const {
  // USER
  setIsLoading,
  setUserAuthState,
  setIsAuthStateObserverMounted,
  setAppUser,
  setUid,
  setEmail,
  setName,
  setEmailVerified,
  setIsAnonymousUser,
  setIsEditor,
  setCreatedOn,
  setModifiedOn,
  setEmailVerifying,
  setErrorMessage,
  resetAppUser,
} = userSlice.actions;

export const getIsLoading = (state: RootState): boolean => state.user.isLoading;
export const isMounted = (state: RootState): boolean => state.user.isAuthStateObserverMounted;
export const getUserAuthState = (state: RootState): UserAuthState => state.user.authState;
export const getIsUserLoggedIn = (state: RootState): boolean => state.user.authState === UserAuthState.LoggedIn;
export const getAppUser = (state: RootState): Maybe<AppUser> => state.user;
export const getErrorMessage = (state: RootState): string => state.user.errorMessage ?? '';
// #region USER ***************************************************************
export const getUid = (state: RootState): string => state.user.uid;
export const getEmail = (state: RootState): string => state.user.email;
export const getName = (state: RootState): string => state.user.name ?? '';
export const getEmailVerified = (state: RootState): boolean => state.user.emailVerified;
export const getIsAnonymousUser = (state: RootState): boolean => state.user.isAnonymous;
export const getIsEditor = memoize((state: RootState): boolean => getIsUserLoggedIn(state) && state.user.isEditor);
export const getCreatedOn = (state: RootState): number => state.user.createdOn;
export const modifiedOn = (state: RootState): number => state.user.modifiedOn;
// #endregion

// #region DISPLAY BASED ON USER STATE SELECTORS ******************************
const isUserAuthenticating = (state: RootState): boolean =>
  state.user.authState === UserAuthState.Authenticating || state.user.isLoading;
const isUserLoggedOut = (state: RootState): boolean => state.user.authState === UserAuthState.LoggedOut;
const isUserNotLoggedIn = (state: RootState): boolean => state.user.authState === UserAuthState.NotLoggedIn;
const isUserInErrorState = (state: RootState): boolean => state.user.authState === UserAuthState.Error;
const isUserInIdleState = (state: RootState): boolean => state.user.authState === UserAuthState.Init;

export const getShowLoggedOutElements = (state: RootState): boolean => isUserLoggedOut(state);
export const getShowNotLoggedInElements = (state: RootState): boolean => isUserNotLoggedIn(state);
export const getShowErrorElements = (state: RootState): boolean => isUserInErrorState(state);
export const getShowIdleElements = (state: RootState): boolean => isUserInIdleState(state);

export const getShowAuthenticatingElements = (state: RootState): boolean => isUserAuthenticating(state);

export const getShowLoggedInElements = (state: RootState): boolean =>
  !state.user.isLoading && state.user.authState === UserAuthState.LoggedIn;

export const getHideLoggedInElements = (state: RootState): boolean =>
  !getShowLoggedInElements &&
  (isUserLoggedOut(state) || isUserNotLoggedIn(state) || isUserInErrorState(state) || isUserInIdleState(state));
// #endregion

export default userSlice.reducer;
